import { useCallback, useMemo } from 'react'
import {
  type RatioId,
  type AreaData,
  type ReportKpiFilter,
  isNotDefined,
  isDefined,
  type RatioData,
  isNotEmpty,
  isTrue,
  getRatioKpi,
  toAdvancedCustomFields,
  unique,
} from '@terros/common'
import { useEffectiveWorkflowStatsFeature } from '../workflow/feature'
import { useSafeCurrentWorkflow } from '../workflow/context'
import { useProfile } from '../user'
import { useDecoratedReportKpiV2, toValidNumberValue } from '../report'
import { useRatios } from '../ratio'
import { type TinyQueryOptions } from '../query'
import { useAccountStatuses } from '../accountStatus'
import { mergeCustomFieldFilters } from '../account'
import { toAreaAccountTotalFilter, useAccountMapTotal } from './totalAccounts'

type AggregatedAreaStats = {
  totalAccounts: number
  assigned?: number
  worked?: number
  pitches?: number
  lastStage?: number
  customStats?: Map<RatioId, number>
}

export type AreaOverviewParams = {
  kpis: string[]
  lastStageKpi?: string
  lastStage?: { name: string }
  customStats?: RatioData[]
  isWorkflowMode: boolean
  isWorkflowStatsLoading: boolean
}

export type AreaOverviewStats = {
  data: AggregatedAreaStats | undefined
  params: AreaOverviewParams
  isLoading: boolean
  refetch: VoidFunction
  isFetching: boolean
}

export const useAreaOverviewStats = (area: AreaData, options?: TinyQueryOptions): AreaOverviewStats => {
  const params = useAreaOverviewParams()
  const { lastStageKpi, customStats, kpis, isWorkflowStatsLoading } = params

  const reportKpiFilter: ReportKpiFilter = useMemo(() => {
    return {
      polygon: isNotEmpty(area.coordinates) ? area.coordinates : undefined,
      ...toAreaReportKpiFilter(area),
    }
  }, [area])

  const { data: rows, ...state } = useDecoratedReportKpiV2(
    {
      grouping: { type: 'company' },
      filter: reportKpiFilter,
      kpis,
      searchByCurrentStatus: true,
    },
    { ...options, enabled: !isWorkflowStatsLoading && options?.enabled !== false }
  )

  const { data: totalAccounts, isLoading: isLoadingTotalAccounts } = useAccountMapTotal(toAreaAccountTotalFilter(area))

  const row = rows?.at(0)
  const getRowValue = useCallback(
    (key: string): number | undefined => {
      if (isNotDefined(row)) return
      return toValidNumberValue(row[key])
    },
    [row]
  )

  const aggregated: AggregatedAreaStats | undefined = useMemo(() => {
    if (isNotDefined(totalAccounts)) return
    return {
      totalAccounts,
      assigned: getRowValue('accounts.assigned'),
      worked: getRowValue('accounts.worked'),
      pitches: getRowValue('accounts.pitches'),
      lastStage: isDefined(lastStageKpi) ? getRowValue(lastStageKpi) : undefined,
      customStats: customStats?.reduce((acc, s) => {
        const value = getRowValue(getRatioKpi(s))
        if (isNotDefined(value)) return acc
        acc.set(s.ratioId, value)
        return acc
      }, new Map<RatioId, number>()),
    }
  }, [lastStageKpi, customStats, totalAccounts, getRowValue])

  return {
    ...state,
    isLoading: state.isLoading || isLoadingTotalAccounts || isWorkflowStatsLoading,
    params,
    data: aggregated,
  }
}

export const useAreaOverviewParams = (): AreaOverviewParams => {
  const wfContext = useSafeCurrentWorkflow()
  const workflowId = wfContext?.workflow.id
  const { isEnabled: workflowStatsEnabled, isLoading: isWorkflowFeatureLoading } = useEffectiveWorkflowStatsFeature()
  const isWorkflowStatsLoading = isDefined(wfContext) && isWorkflowFeatureLoading
  const { user } = useProfile()
  const { data: customStats } = useRatios({ scopeId: user?.companyId, workflowId })
  const legacyLastStage = useAccountStatuses().data?.statuses.find((s) => isTrue(s.isFinalState))

  const workflowFinalStage = useMemo(() => {
    if (isNotDefined(wfContext)) return undefined
    return wfContext.workflow.pipelineStages.findLast((stage) => isTrue(stage.reportConfig.count))
  }, [wfContext])

  const lastStageKpi = useMemo(() => {
    if (isDefined(wfContext)) {
      if (isNotDefined(workflowFinalStage)) return undefined
      const entity = workflowStatsEnabled ? 'account' : 'setter'
      return `${entity}|${workflowFinalStage.id}|count`
    }
    if (isNotDefined(legacyLastStage)) return undefined
    return `setter.${legacyLastStage.statusId}.count`
  }, [legacyLastStage, wfContext, workflowFinalStage, workflowStatsEnabled])

  const lastStage = useMemo(() => {
    if (isDefined(wfContext)) return workflowFinalStage
    return legacyLastStage
  }, [wfContext, workflowFinalStage, legacyLastStage])

  const kpis = useMemo(() => {
    const kpis = ['groupBy', 'accounts.created', 'accounts.assigned', 'accounts.worked']
    if (isNotDefined(wfContext)) {
      kpis.push('accounts.pitches')
    }
    if (isDefined(lastStageKpi)) {
      kpis.push(lastStageKpi)
    }
    if (isNotEmpty(customStats)) {
      kpis.push(...customStats.map(getRatioKpi))
    }
    return kpis
  }, [lastStageKpi, customStats, wfContext])

  return {
    kpis,
    lastStageKpi,
    lastStage,
    customStats,
    isWorkflowMode: isDefined(wfContext),
    isWorkflowStatsLoading,
  }
}

const toAreaReportKpiFilter = ({
  filters,
  teamId,
  userIds,
}: Pick<AreaData, 'filters' | 'teamId' | 'userIds'>): Omit<ReportKpiFilter, 'polygon'> => {
  if (isNotDefined(filters)) return {}

  const {
    teamIds: filterTeams,
    userIds: filterUsers,
    customFields,
    advancedCustomFields,
    statusIds: _statusIds,
    workflowStageIds: _workflowStageIds,
    ...reportCompatibleFilters
  } = filters

  const teams = unique([...(filterTeams ?? []), ...(isDefined(teamId) ? [teamId] : [])])
  const users = unique([...(filterUsers ?? []), ...(userIds ?? [])])

  return {
    ...reportCompatibleFilters,
    teams: isNotEmpty(teams) ? teams : undefined,
    users: isNotEmpty(users) ? users : undefined,
    customFields: mergeCustomFieldFilters(toAdvancedCustomFields(customFields), advancedCustomFields),
  }
}
